import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';

import {
  useSignUpFlowContext,
} from 'Views/Onboarding/OnboardingHasUser/OnboardingHasUserContent/OnboardingHasUserContentRouter/SignUpFlow/SignUpFlowContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import { useOnboardingContext } from '../../Contexts/OnboardingContext';

const ClientAgreementContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const ACCEPT_STARTED = 'accept-started';
export const ACCEPT_SUCCEEDED = 'accept-succeeded';
export const ACCEPT_FAILED = 'accept-failed';

export const BILLING_INFORMATION_REFRESH_STARTED = 'client-refresh-started';
export const BILLING_INFORMATION_REFRESH_SUCCEEDED = 'client-refresh-succeeded';
export const BILLING_INFORMATION_REFRESH_FAILED = 'client-refresh-failed';

export const TOGGLE_AGREEMENT_ACCEPTED = 'toggle-agreement-accepted';
const SET_SIGNED_AGREEMENT = 'set-signed-agreement';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isAccepting: false,
    acceptingError: null,
    billingInformationCollection: null,
    isBillingInformationRefreshing: false,
    billingInformationRefreshError: null,
    agreementsAcceptedMap: {},
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        billingInformationCollection: action.billingInformationCollection,
        plannerResource: action.plannerResource,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case ACCEPT_STARTED: {
      return {
        ...state,
        isAccepting: true,
        acceptingError: null,
      };
    }
    case ACCEPT_SUCCEEDED: {
      return {
        ...state,
        isAccepting: false,
        acceptingError: null,
      };
    }
    case ACCEPT_FAILED: {
      return {
        ...state,
        isAccepting: false,
        acceptingError: action.acceptingError,
      };
    }
    case BILLING_INFORMATION_REFRESH_STARTED: {
      return {
        ...state,
        isBillingInformationRefreshing: true,
        billingInformationRefreshError: null,
      };
    }
    case BILLING_INFORMATION_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isBillingInformationRefreshing: false,
        billingInformationRefreshError: null,
        billingInformationCollection: action.billingInformationCollection,
      };
    }
    case BILLING_INFORMATION_REFRESH_FAILED: {
      return {
        ...state,
        isBillingInformationRefreshing: false,
        billingInformationRefreshError: action.billingInformationRefreshError,
      };
    }
    case TOGGLE_AGREEMENT_ACCEPTED: {
      return {
        ...state,
        agreementsAcceptedMap: {
          ...state.agreementsAcceptedMap,
          [action.clientResource.id()]: action.accepted,
        },
      };
    }
    case SET_SIGNED_AGREEMENT: {
      const billingInformationResource = state.billingInformationCollection?.resources().head();
      billingInformationResource.attr('signedAgreement', action.value);
      return { ...state };
    }
    default: {
      throw new Error(`Unhandled client-agreement-context action type: ${action.type}`);
    }
  }
};

function checkAllAgreementsAcceptedInDeprecatedFlow(householdResource, state) {
  return householdResource.attr('clients').resources().list().reduce((acc, curr) => {
    return acc && state.agreementsAcceptedMap[curr.id()];
  }, true);
}
function checkAllAgreementsAcceptedInNewFlow(billingInformationResource) {
  const requiredSignatures = billingInformationResource.meta().field('signedAgreement').options().length();
  const actualSignatures = (billingInformationResource.attr('signedAgreement') ?? []).length;
  return actualSignatures === requiredSignatures;
}

const ClientAgreementProvider = ({
  children,
}) => {
  const { clientResource, householdResource } = useOnboardingContext();
  const { activeStep, refreshSignUpStepsCollection } = useSignUpFlowContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'Client Agreement',
    isPageView: true,
  });

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const { billingInformationCollection } = state;
  const handleAcceptAgreement = React.useCallback(async () => {
    try {
      dispatch({ type: ACCEPT_STARTED });

      await activeStep.relationship(fr.relationships.relatedTask.name)
        .link('related')
        .Post(billingInformationCollection.resources().head().export(), { deprecatedAllowAlterOfRelation: true });

      await refreshSignUpStepsCollection();

      logStandardizedEvent(categories.digitalOnboarding, eventTypes.completeStep, eventSubTypes.success, { eventProperties: { stepName: 'client agreement' } });

      dispatch({ type: ACCEPT_SUCCEEDED });
    } catch (err) {
      dispatch({ type: ACCEPT_FAILED, acceptingError: err });
    }
  }, [activeStep, refreshSignUpStepsCollection, billingInformationCollection]);

  const refreshBillingInformationCollection = React.useCallback(async () => {
    try {
      dispatch({ type: BILLING_INFORMATION_REFRESH_STARTED });
      const coll = await householdResource.relationship(fr.relationships.billingInformation.name).link('related').get();
      dispatch({ type: BILLING_INFORMATION_REFRESH_SUCCEEDED, billingInformationCollection: coll });
    } catch (err) {
      dispatch({ type: BILLING_INFORMATION_REFRESH_FAILED, billingInformationRefreshError: err });
    }
  }, [householdResource]);

  const memoizedValue = React.useMemo(() => {
    const billingInformationResource = state.billingInformationCollection?.resources().head();
    const usingNewAgreementFlow = billingInformationResource?.meta()?.field('signedAgreement');
    const agreementsAccepted = usingNewAgreementFlow
      ? checkAllAgreementsAcceptedInNewFlow(billingInformationResource)
      : checkAllAgreementsAcceptedInDeprecatedFlow(householdResource, state);

    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      plannerResource: state.plannerResource,
      billingInformationCollection: state.billingInformationCollection,
      billingInformationResource,
      isBillingInformationRefreshing: state.isBillingInformationRefreshing,
      billingInformationRefreshError: state.billingInformationRefreshError,
      refreshBillingInformationCollection,
      isAccepting: state.isAccepting,
      acceptingError: state.acceptingError,
      agreementsAccepted,
      hasClientAccepted: (clientRes) => {
        return !!state.agreementsAcceptedMap[clientRes.id()];
      },
      handleAcceptAgreement,
      handleSetAgreementToggle: (clientRes, accepted) => {
        dispatch({
          type: TOGGLE_AGREEMENT_ACCEPTED,
          clientResource: clientRes,
          accepted,
        });
      },
      isAcceptAgreementDisabled: !agreementsAccepted,
      setSignedAgreement(value) {
        dispatch({ type: SET_SIGNED_AGREEMENT, value });
      },
    };
  }, [
    state,
    clientResource,
    householdResource,
    handleAcceptAgreement,
    refreshBillingInformationCollection,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });
      const [billingInformationResp, plannerResp] = await Promise.all([
        householdResource.relationship(fr.relationships.billingInformation.name).link('related').get(),
        householdResource.relationship(fr.relationships.planner.name).link('related').get(),
      ]);

      const [plannerResource] = plannerResp;

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        billingInformationCollection: billingInformationResp,
        plannerResource,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <ClientAgreementContext.Provider value={memoizedValue}>
      {children}
    </ClientAgreementContext.Provider>
  );
};

// Use ClientAgreement State Context
const useClientAgreementContext = () => {
  const context = React.useContext(ClientAgreementContext);
  if (context === undefined) {
    throw new Error('useClientAgreementContext must be used within a ClientAgreementProvider');
  }
  return context;
};

ClientAgreementProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  ClientAgreementProvider,
  useClientAgreementContext,
};
